import React, {CSSProperties, FunctionComponent, ReactNode, useContext, useRef} from 'react';
import classNames from 'classnames';
import {Icon} from '../Icon';
import AccordionContext from './AccordionContext';
import {IconPlaceholder} from "../Icon/IconPlaceholder";
import {TableActionDropdownMenu} from "../Table/TableActionDropdownMenu";
import {useAnalyticsTrackers} from "../../utils/useAnalyticsTrackers";

type AccordionItemProps = {
    children?: ReactNode,
    uuid: string,
    buttonClassName?: string,
    className?: string,
    accentColor?: string,
    primaryText?: string,
    rightAlignedContent?: ReactNode,
    HeaderComponent?: FunctionComponent<{ expanded: boolean }>,
    accordionHeight?: string,
    expandable?: boolean,
    actionMenu?: ReactNode,
};

export default function AccordionItemWithActionMenu({
                                          children,
                                          uuid,
                                          buttonClassName,
                                          HeaderComponent,
                                          className,
                                          accentColor,
                                          rightAlignedContent,
                                          primaryText,
                                          accordionHeight,
                                          expandable = true,
                                          actionMenu = <div style={{visibility:"hidden"}}><TableActionDropdownMenu ariaLabel={""}/></div>
                                      }: AccordionItemProps) {
    const {expandedItems, toggleItem} = useContext(AccordionContext);
    const openTimeRef = useRef<number | null>(null);
    const { page } = useAnalyticsTrackers("enableAssetsGoogleAnalytics");
    const isExpanded = expandedItems.includes(uuid);

    const toggle = () => {
        if (expandable) {
            toggleItem(uuid);
        }
        if (!isExpanded) {
            openTimeRef.current = window.performance.now();
        } else if (openTimeRef.current && uuid) {
            const duration = Math.round((window.performance.now() - openTimeRef.current) / 1000);
            page(`Expand_${uuid}_Accordion`, {cd4_duration: duration});
        }
    }


    const iconName = isExpanded ? 'chevron_down' : 'chevron_right';
    const icon = expandable ? <Icon name={iconName} size="large"/> : <IconPlaceholder/>;
    const stylesForHeaderContainer: CSSProperties = {display: "flex", flexDirection: "row", alignItems: "center", backgroundColor: "white"};
    const Header = (HeaderComponent)
        ? <HeaderComponent expanded={isExpanded}/>
        : (
            <div className="accented-accordion-item__header">
                <span className="accented-accordion-item__left-content">
                    {icon}
                    <span className="paddingleft-md">
                        <div className="h4 fontweight-normal margin-xs">{primaryText}</div>
                    </span>
                </span>
                {rightAlignedContent && (
                    <span className="accented-accordion-item__right-content">{rightAlignedContent}</span>
                )}
            </div>
        );

    const AccordionItemWithoutAccent = (
        <li
            className={classNames(
                'accordion__item',
                {'accordion__item--expanded': isExpanded, 'accordion__item--collapsed': !isExpanded},
                accentColor ? className : null,
            )}
        >
            <div style={stylesForHeaderContainer}>
                <div
                    className={classNames('accordion__button', buttonClassName, expandable && 'accordion__button--expandable')}
                    aria-pressed={isExpanded ? 'true' : 'false'}
                    onClick={toggle}
                    role="button"
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            toggle();
                        }
                    }}
                    tabIndex={0}
                >
                    {Header}
                </div>
                {actionMenu}
            </div>
            <div className="accordion__content">{children}</div>
        </li>
    );

    if (accentColor) {
        return (
            <div style={{minHeight: accordionHeight}}
                 role="accordionitem"
                 className={classNames('accented-accordion-item', className)}>
                <div aria-label="accordion accent color"
                     className="accented-accordion-item__accent"
                     style={{backgroundColor: accentColor, minHeight: accordionHeight}} />
                {AccordionItemWithoutAccent}
            </div>
        );
    } else {
        return AccordionItemWithoutAccent;
    }
}
